import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import DemoScrollNavigationMenu from "./components/ui/demo-scroll-navigation-menu";
import { DemoHeroGeometric } from "./components/ui/demo-hero-geometric";
import DemoFooter from "./components/ui/demo-footer-section";
import WhyUsSection from "./components/ui/why-us-section";
import { TestimonialSection } from "./components/ui/demo-testimonial-carousel";
import { ContactInfoSection } from "./components/ui/contact-info-section";
import { ProjectsSection } from "./components/ui/projects-section";
import { ServicesSection } from "./components/ui/services-section";
import NotFoundPage from "./components/ui/page-not-found";
import ScrollReveal from "./components/ui/scroll-reveal";
import AboutPage from "./pages/about";
import FaqsPage from "./pages/faqs";
import HelpPage from "./pages/help";

const SITE_URL = "https://aimrane.digital";

const SEO_BY_PATH = {
  "/": {
    title: "Freelance web | Creation site web | Aimrane Digital",
    description:
      "Freelance web: creation de site web, landing pages, SEO et developpement sur mesure pour entreprises et independants.",
  },
  "/about": {
    title: "A propos | Freelance web | Aimrane Digital",
    description:
      "Decouvrez mon approche de freelance web pour la creation de site web, la performance et le SEO.",
  },
  "/faqs": {
    title: "FAQ creation site web | Aimrane Digital",
    description:
      "Questions frequentes sur la creation de site web, les delais, le SEO et la maintenance.",
  },
  "/help": {
    title: "Aide & contact | Aimrane Digital",
    description:
      "Besoin d'aide pour un projet web ? Contactez un freelance web pour un accompagnement clair et rapide.",
  },
};

function updateMetaTag(selector, content) {
  const node = document.querySelector(selector);
  if (node) node.setAttribute("content", content);
}

function updateCanonical(url) {
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.setAttribute("href", url);
}

function RouteSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const config = SEO_BY_PATH[pathname] ?? {
      title: "Page introuvable | Aimrane Digital",
      description:
        "Page introuvable. Revenez vers les services de creation de site web et contact sur Aimrane Digital.",
    };

    const fullTitle = config.title.includes("Aimrane Digital")
      ? config.title
      : `${config.title} | Aimrane Digital`;
    const url = `${SITE_URL}${pathname}`;

    document.title = fullTitle;
    updateMetaTag('meta[name="description"]', config.description);
    updateMetaTag('meta[property="og:title"]', fullTitle);
    updateMetaTag('meta[property="og:description"]', config.description);
    updateMetaTag('meta[property="og:url"]', url);
    updateMetaTag('meta[name="twitter:title"]', fullTitle);
    updateMetaTag('meta[name="twitter:description"]', config.description);
    updateMetaTag('meta[name="twitter:url"]', url);
    updateCanonical(url);
  }, [pathname]);

  return null;
}

function ScrollToHashOnHome() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (pathname !== "/" || !hash) return;

    const id = hash.replace("#", "");

    const scrollToElement = () => {
      const el = document.getElementById(id);
      if (!el) return false;
      const nav = document.getElementById("site-nav");
      const navH = nav ? nav.getBoundingClientRect().height : 0;
      const y = el.getBoundingClientRect().top + window.scrollY - navH - 12;
      window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
      return true;
    };

    // Attempt 1 : prochain repaint (clic hash sur la même page)
    requestAnimationFrame(() => {
      if (scrollToElement()) return;

      // Attempt 2 : après 100ms (navigation depuis une autre route)
      const t1 = setTimeout(() => {
        if (scrollToElement()) return;

        // Attempt 3 : après 400ms (composants lazy ou rendus lents)
        const t2 = setTimeout(scrollToElement, 300);
        return () => clearTimeout(t2);
      }, 100);

      return () => clearTimeout(t1);
    });
  }, [pathname, hash]);

  return null;
}

function ScrollToTopOnRouteChange() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    // Keep hash-based navigation on home handled by ScrollToHashOnHome.
    if (pathname === "/" && hash) return;
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
}

function SiteLayout({ children }) {
  return (
    <div id="top" className="App dark min-h-screen bg-surface text-zinc-200">
      <RouteSeo />
      <ScrollToTopOnRouteChange />
      <ScrollToHashOnHome />
      <DemoScrollNavigationMenu />
      {children}
      <DemoFooter />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <SiteLayout>
              <ScrollReveal delay={0} distance={10} threshold={0.05}>
                <DemoHeroGeometric />
              </ScrollReveal>
              <ScrollReveal delay={70}>
                <ServicesSection />
              </ScrollReveal>
              <ScrollReveal delay={100}>
                <ProjectsSection />
              </ScrollReveal>
              <ScrollReveal delay={130}>
                <WhyUsSection />
              </ScrollReveal>
              <ScrollReveal delay={160}>
                <TestimonialSection />
              </ScrollReveal>
              <ScrollReveal delay={190}>
                <ContactInfoSection />
              </ScrollReveal>
            </SiteLayout>
          }
        />
        <Route path="/about" element={<SiteLayout><AboutPage /></SiteLayout>} />
        <Route path="/faqs" element={<SiteLayout><FaqsPage /></SiteLayout>} />
        
        <Route path="/help" element={<SiteLayout><HelpPage /></SiteLayout>} />
        <Route path="/blog" element={<NotFoundPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;